import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  // Employee OTP Auth
  sendOTP: (data) => api.post('/auth/employee/send-otp', data),
  verifyOTP: (data) => api.post('/auth/employee/verify-otp', data),
  
  // Admin Auth
  adminLogin: (data) => api.post('/auth/admin/login', data),
  createAdmin: (data) => api.post('/auth/admin/create', data),
  getAdminUsers: () => api.get('/auth/admin/users'),
  deleteAdmin: (id) => api.delete(`/auth/admin/${id}`),
  
  // Password Management
  forgotPassword: (data) => api.post('/auth/admin/forgot-password', data),
  resetPassword: (data) => api.post('/auth/admin/reset-password', data),
  changePassword: (data) => api.post('/auth/admin/change-password', data),
  resetUserPassword: (id, data) => api.post(`/auth/admin/${id}/reset-password`, data || {}),
  assignSurveys: (id, surveyIds) => api.put(`/auth/admin/${id}/assigned-surveys`, { survey_ids: surveyIds }),
  setRawDataAccess: (id, allow) => api.put(`/auth/admin/${id}/raw-data-access`, { raw_data_access: allow }),
};

// Employee APIs
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getCount: (params) => api.get('/employees/count', { params }),
  getDepartments: () => api.get('/employees/departments'),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  bulkUpload: (data) => api.post('/employees/bulk-upload', data),
};

// Survey APIs
export const surveyAPI = {
  // Admin
  getAll: (params) => api.get('/surveys', { params }),
  getById: (id) => api.get(`/surveys/${id}`),
  create: (data) => api.post('/surveys', data),
  update: (id, data) => api.put(`/surveys/${id}`, data),
  delete: (id) => api.delete(`/surveys/${id}`),
  deactivate: (id) => api.put(`/surveys/${id}/deactivate`),
  reactivate: (id) => api.put(`/surveys/${id}/reactivate`),
  clearResponses: (id) => api.delete(`/surveys/${id}/responses`),
  deleteResponse: (surveyId, employeeId) => api.delete(`/surveys/${surveyId}/responses/${employeeId}`),
  updateSectionLabels: (id, labels) => api.put(`/surveys/${id}/section-labels`, labels),
  updateSectionWeights: (id, weights) => api.put(`/surveys/${id}/section-weights`, weights),
  updateSectionGroups: (id, groups) => api.put(`/surveys/${id}/section-groups`, groups),
  addQuestion: (surveyId, data) => api.post(`/surveys/${surveyId}/questions`, data),
  updateQuestion: (surveyId, questionId, data) => api.put(`/surveys/${surveyId}/questions/${questionId}`, data),
  deleteQuestion: (surveyId, questionId) => api.delete(`/surveys/${surveyId}/questions/${questionId}`),
  
  // Employee
  getAvailable: () => api.get('/employee/surveys'),
  getSurveyForEmployee: (id) => api.get(`/employee/surveys/${id}`),
  submit: (id, data) => api.post(`/employee/surveys/${id}/submit`, data),
  autoSave: (id, data) => api.post(`/employee/surveys/${id}/auto-save`, data),
  getDraft: (id) => api.get(`/employee/surveys/${id}/draft`),
};

// Analytics APIs
export const analyticsAPI = {
  getSurveyAnalytics: (id, params) => api.get(`/analytics/surveys/${id}`, { params }),
  getHeatmap: (id, params) => api.get(`/analytics/heatmap/${id}`, { params }),
  getIndividualResponses: (id, params) => api.get(`/analytics/surveys/${id}/responses`, { params }),
  getParticipation: (id, params) => api.get(`/analytics/surveys/${id}/participation`, { params }),
  getComments: (id, params) => api.get(`/analytics/surveys/${id}/comments`, { params }),
  analyzeComments: (id) => api.post(`/analytics/surveys/${id}/comments/analyze`),
  getTrends: (months) => api.get('/analytics/trends', { params: { months } }),
  getComparison: () => api.get('/analytics/comparison'),
};

// Action Tracker APIs
export const actionAPI = {
  getAll: (params) => api.get('/actions', { params }),
  create: (data) => api.post('/actions', data),
  update: (id, data) => api.put(`/actions/${id}`, data),
  delete: (id) => api.delete(`/actions/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (params) => api.get('/dashboard/stats', { params }),
};

// Notification APIs
export const notificationAPI = {
  getLogs: (params) => api.get('/notifications/logs', { params }),
  sendInvitations: (data) => api.post('/notifications/send-invitations', data),
};

// Audit APIs
export const auditAPI = {
  getLogs: (params) => api.get('/audit/logs', { params }),
};

// System APIs
export const systemAPI = {
  initialize: () => api.post('/init/setup'),
  healthCheck: () => api.get('/health'),
};

export default api;
